// 11 §4.1 — board camera geometry, and the board→screen mapping the overlay layers use:
// screen = (world − scroll) × zoom. docs/33 E4–E6: the canvas renders at the device's pixel ratio
// (text and strokes stay sharp on HiDPI screens), and the encounter zooms through stops that land
// on whole device pixels so the art never shears; `zoom` here is always CSS px per art px.
// docs/42: the player's own zoom is free-flowing between the bounds (wheel, pinch, Q/E); the stops
// are where the camera starts (fit, the default zoom) and what the face-off frames.
import type Phaser from 'phaser';
import { BOARD_CAMERA_ZOOMS, BOARD_ZOOM, type BoardZoom } from '../../config';
import type { Tile } from '../../core/sim/types';

/** `top`: board px the art rises above row 0 (docs/186 EA3: crowns and raised walls), shown as part of the board. */
export interface BoardGeometry { w: number; h: number; cell: number; top?: number }

/** Device pixels per CSS pixel, as the canvas is rendered (1–3). */
export function pixelRatio(): number {
  const r = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return Math.min(3, Math.max(1, Math.round(r * 4) / 4));
}

/** Every zoom between the bounds where one art pixel is a whole number of device pixels. */
export function zoomStops(dpr = pixelRatio()): number[] {
  const out: number[] = [];
  for (let k = Math.ceil(BOARD_ZOOM.min * dpr); k <= Math.floor(BOARD_ZOOM.max * dpr); k++) out.push(k / dpr);
  return out;
}

/** The nearest stop `steps` away from `zoom` (±1 = one stop in or out). */
export function stepZoom(zoom: number, steps: number, dpr = pixelRatio()): number {
  const stops = zoomStops(dpr);
  if (steps === 0) return stops.reduce((a, b) => (Math.abs(b - zoom) < Math.abs(a - zoom) ? b : a));
  const way = steps > 0 ? stops.filter((z) => z > zoom + 1e-6) : stops.filter((z) => z < zoom - 1e-6).reverse();
  return way[Math.min(way.length, Math.abs(steps)) - 1] ?? (steps > 0 ? stops[stops.length - 1]! : stops[0]!);
}

/** A zoom kept inside the bounds. */
export function clampZoom(zoom: number): number { return Math.min(BOARD_ZOOM.max, Math.max(BOARD_ZOOM.min, zoom)); }

/** docs/42 CM2: one press of + / − or a zoom button scales by this. */
export const ZOOM_STEP = 1.25;
/** docs/42 CM3: Q / E held zoom by this much per second. */
export const ZOOM_KEY_RATE = 2.4;

/**
 * docs/42 CM1: how much one wheel event scales the zoom (>1 = in). A mouse notch (~100 px, or one
 * line / page) is ~1.15×; a trackpad pinch arrives as small ctrl+wheel deltas and is made more
 * sensitive so the board tracks the fingers. Big bursts are capped so a flicked wheel can't jump.
 */
export function wheelZoomFactor(e: { deltaY: number; deltaMode: number; ctrlKey: boolean }): number {
  const px = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
  const d = Math.max(-240, Math.min(240, px));
  return Math.exp(-d * (e.ctrlKey ? 0.012 : 0.0014));
}

/**
 * docs/42 CM5: which way a pointer at (x, y) in a `w × h` window pushes the camera: each axis 0
 * outside the `band` (CSS px) along that edge, ±0.5 entering it, ±1 at the very edge. Corners push both ways.
 */
export function edgePush(x: number, y: number, w: number, h: number, band: number): [number, number] {
  const depth = (gap: number) => 0.5 + 0.5 * (1 - Math.max(0, gap) / band);
  const axis = (v: number, size: number) => (v < band ? -depth(v) : v > size - 1 - band ? depth(size - 1 - v) : 0);
  return [axis(x, w), axis(y, h)];
}

/**
 * docs/42 CM5: edge panning that a quick reach for a menu never sets off. The pointer has to rest
 * in the edge band for `dwell` seconds before anything moves, then the speed eases in over `ramp`
 * seconds; leaving the band (or `push` turning [0, 0]) stops at once and starts the dwell again.
 * Each frame counts for at most 50 ms, so a hitch can only lengthen the rest, never shorten it.
 */
export class EdgePan {
  private held = 0;
  constructor(private readonly dwell = 0.3, private readonly ramp = 0.45) {}
  /** The pan direction scaled 0..1 by the ease-in, for this frame. */
  step(dt: number, push: [number, number]): [number, number] {
    if (push[0] === 0 && push[1] === 0) { this.held = 0; return [0, 0]; }
    // a long frame never counts for much of the rest (it may mostly predate the pointer's arrival)
    this.held += Math.min(dt, 0.05);
    const t = Math.min(1, Math.max(0, (this.held - this.dwell) / this.ramp));
    const e = t * t * (3 - 2 * t);
    return [push[0] * e, push[1] * e];
  }
  reset(): void { this.held = 0; }
}

/** The largest stop at which the whole board fits the view (CSS px), never below the minimum. */
export function fitStop(board: BoardGeometry, viewW: number, viewH: number, padding = 48, dpr = pixelRatio()): number {
  const fit = Math.min((viewW - padding) / (board.w * board.cell), (viewH - padding) / (board.h * board.cell + (board.top ?? 0)));
  const stops = zoomStops(dpr);
  return [...stops].reverse().find((z) => z <= fit) ?? stops[0]!;
}

/** The gallery's integer fit (2–4). */
export function fitZoom(board: BoardGeometry, viewW: number, viewH: number, padding = 48): BoardZoom {
  const fit = Math.min((viewW - padding) / (board.w * board.cell), (viewH - padding) / (board.h * board.cell + (board.top ?? 0)));
  const clamped = Math.max(2, Math.min(4, Math.floor(fit)));
  return (BOARD_CAMERA_ZOOMS.includes(clamped as BoardZoom) ? clamped : 2) as BoardZoom;
}

export interface ScreenRect { x: number; y: number; w: number; h: number }

/** The scroll that centres the board in `rect` (CSS px) at `zoom`. */
export function centredScroll(board: BoardGeometry, zoom: number, rect: ScreenRect): { x: number; y: number } {
  return { x: (board.w * board.cell) / 2 - (rect.x + rect.w / 2) / zoom, y: (board.h * board.cell - (board.top ?? 0)) / 2 - (rect.y + rect.h / 2) / zoom };
}

/**
 * Keep at least half of whichever is smaller — the board or the view — in the view, so the
 * player can pan to any edge without losing the board.
 */
export function clampScroll(board: BoardGeometry, zoom: number, rect: ScreenRect, scroll: { x: number; y: number }): { x: number; y: number } {
  const axis = (v: number, size: number, lo: number, span: number) => {
    const m = Math.min(span, size * zoom) / 2;
    const min = -(lo + span - m) / zoom, max = size - (lo + m) / zoom;
    return Math.max(min, Math.min(max, v));
  };
  const top = board.top ?? 0;
  return { x: axis(scroll.x, board.w * board.cell, rect.x, rect.w), y: axis(scroll.y + top, board.h * board.cell + top, rect.y, rect.h) - top };
}

export class BoardMapper {
  /** `zoom()` is the CSS zoom (the camera's own zoom also carries the pixel ratio). */
  constructor(public readonly cam: Phaser.Cameras.Scene2D.Camera, public readonly cell: number, private readonly zoomOf: () => number = () => cam.zoom) {}
  get zoom(): number { return this.zoomOf(); }
  /** World (board) pixel of the tile's top-left. */
  tileOrigin(t: Tile): { x: number; y: number } { return { x: t.x * this.cell, y: t.y * this.cell }; }
  /** World pixel of the tile centre. */
  tileCentre(t: Tile): { x: number; y: number } { return { x: t.x * this.cell + this.cell / 2, y: t.y * this.cell + this.cell / 2 }; }
  /** World pixel of the unit pivot (bottom centre of the occupied cell). */
  pivot(t: Tile): { x: number; y: number } { return { x: t.x * this.cell + this.cell / 2, y: (t.y + 1) * this.cell }; }
  /** Board→screen (CSS px). */
  toScreen(wx: number, wy: number): { x: number; y: number } {
    return { x: Math.round((wx - this.cam.scrollX) * this.zoom), y: Math.round((wy - this.cam.scrollY) * this.zoom) };
  }
  screenToTile(sx: number, sy: number): Tile {
    const wx = sx / this.zoom + this.cam.scrollX, wy = sy / this.zoom + this.cam.scrollY;
    return { x: Math.floor(wx / this.cell), y: Math.floor(wy / this.cell) };
  }
  /** Screen-space size of one cell (CSS px). */
  get cellPx(): number { return this.cell * this.zoom; }
}

/**
 * Centre a board of the given size inside `rect` (screen px; defaults to the whole camera) at the
 * given zoom. The camera origin is (0,0) so screen = (world − scroll) × zoom; scroll is rounded to
 * whole screen pixels (§4.1 "scrolls in whole screen pixels"). Used by the gallery.
 */
export function centreBoard(cam: Phaser.Cameras.Scene2D.Camera, board: BoardGeometry, zoom: number, rect?: ScreenRect): void {
  cam.setZoom(zoom);
  const r = rect ?? { x: 0, y: 0, w: cam.width, h: cam.height };
  const s = centredScroll(board, zoom, r);
  cam.setScroll(Math.round(s.x * zoom) / zoom, Math.round(s.y * zoom) / zoom);
}
